/**
 * Offloads blend file parsing to a Web Worker so the main thread stays
 * responsive (loading spinner keeps animating, UI doesn't freeze).
 */

import {
  callWorker,
  createSingletonWorker,
} from "../workers/createWorkerRPC.ts";
import type { BlendFile } from "./types/BlendFile.ts";

const getWorker = createSingletonWorker(
  () =>
    new Worker(new URL("./parseBlendFile.worker.ts", import.meta.url), {
      type: "module",
    }),
);

/**
 * Parse a .blend file asynchronously in a Web Worker.
 *
 * The ArrayBuffer is transferred (zero-copy) to the worker, so it
 * becomes detached in the calling context.  If the caller still needs
 * the buffer afterwards it should slice() a copy first.
 */
export async function parseBlendFileAsync(
  buffer: ArrayBuffer,
): Promise<BlendFile> {
  const { blendFile } = await callWorker<ArrayBuffer, { blendFile: BlendFile }>(
    getWorker(),
    buffer,
    [buffer],
    {
      stallWarningMs: 8_000,
      stallMessage: "Blend file parsing is taking longer than expected.",
    },
  );
  return blendFile;
}
